<template>
  <div class="layout-container">
    <Sidebar />
    <div class="main-content">
      <div class="landing-items-container">
        <div class="header-actions">
          <h2 class="title">Landing Items</h2>
          <button class="create-button" @click="openCreateModal">
            <span class="icon">+</span> Create New Item
          </button>
        </div>
        
        <div class="table-container">
          <table class="landing-items-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Icon</th>
                <th>Landing Title</th>
                <th>Text</th>
                <th>Text AR</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in landingItems" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.icon }}</td>
                <td>{{ getLandingTitle(item.landing_id) }}</td>
                <td>{{ item.text }}</td>
                <td>{{ item.text_ar }}</td>
                <td>{{ formatDate(item.created_at) }}</td>
                <td>{{ formatDate(item.updated_at) }}</td>
                <td class="actions-column">
                  <button class="edit-button" @click="openEditModal(item)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button class="delete-button" @click="confirmDelete(item)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="landingItems.length === 0">
                <td colspan="8" class="no-data">No landing items found</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="loading" class="loading">
          Loading...
        </div>
        <div v-if="error" class="error">
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Edit Landing Item</h3>
          <button class="close-button" @click="closeEditModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label for="landing_id">Landing</label>
              <select id="landing_id" v-model="editingItem.landing_id" required>
                <option v-for="landing in landings" :key="landing.id" :value="landing.id">
                  {{ landing.title }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="icon">Icon</label>
              <input type="text" id="icon" v-model="editingItem.icon" required>
            </div>
            <div class="form-group">
              <label for="text">Text</label>
              <QuillEditor id="text" v-model:content="editingItem.text" label="Text" :toolbar="toolbarOptions" placeholder="Enter item text" required />
            </div>
            <div class="form-group">
              <label for="text_ar">Text (Arabic)</label>
              <QuillEditor id="text_ar" v-model:content="editingItem.text_ar" label="Text (Arabic)" :toolbar="toolbarOptions" placeholder="Enter Arabic text" required />
            </div>
            <div class="form-actions">
              <button type="button" class="cancel-button" @click="closeEditModal">Cancel</button>
              <button type="submit" class="save-button" :disabled="saving">Save Changes</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Create Landing Item</h3>
          <button class="close-button" @click="closeCreateModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleCreateSubmit">
            <div class="form-group">
              <label for="new_landing_id">Landing</label>
              <select id="new_landing_id" v-model="newItem.landing_id" required>
                <option v-for="landing in landings" :key="landing.id" :value="landing.id">
                  {{ landing.title }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="new_icon">Icon</label>
              <input type="text" id="new_icon" v-model="newItem.icon" required>
              <small class="help-text">Enter icon name (e.g., checkmark, star, etc.)</small>
            </div>
            <div class="form-group">
              <label for="new_text">Text</label>
              <QuillEditor id="new_text" v-model:content="newItem.text" label="Text" :toolbar="toolbarOptions" placeholder="Enter item text" required />
            </div>
            <div class="form-group">
              <label for="new_text_ar">Text (Arabic)</label>
              <QuillEditor id="new_text_ar" v-model:content="newItem.text_ar" label="Text (Arabic)" :toolbar="toolbarOptions" placeholder="Enter Arabic text" required />
            </div>
            <div class="form-actions">
              <button type="button" class="cancel-button" @click="closeCreateModal">Cancel</button>
              <button type="submit" class="save-button" :disabled="creating">Create Item</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <h3>Delete Landing Item</h3>
          <button class="close-button" @click="closeDeleteModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="delete-warning">Are you sure you want to delete this landing item?</p>
          <p class="delete-details">This action cannot be undone.</p>
          
          <div class="item-preview">
            <div class="preview-row">
              <strong>ID:</strong> {{ itemToDelete?.id }}
            </div>
            <div class="preview-row">
              <strong>Icon:</strong> {{ itemToDelete?.icon }}
            </div>
            <div class="preview-row">
              <strong>Text:</strong> {{ itemToDelete?.text }}
            </div>
          </div>
          
          <div class="form-actions">
            <button type="button" class="cancel-button" @click="closeDeleteModal">Cancel</button>
            <button 
              type="button" 
              class="delete-confirm-button" 
              :disabled="deleting"
              @click="handleDelete"
            >
              {{ deleting ? 'Deleting...' : 'Delete Item' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'LandingItems',
  components: {
    Sidebar,
    QuillEditor
  },
  setup() {
    const landingItems = ref([]);
    const landings = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const showEditModal = ref(false);
    const showCreateModal = ref(false);
    const showDeleteModal = ref(false);
    const editingItem = ref(null);
    const itemToDelete = ref(null);
    const toolbarOptions = ref([
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]);
    const newItem = ref({
      landing_id: 1,
      icon: '',
      text: '',
      text_ar: ''
    });
    const saving = ref(false);
    const creating = ref(false);
    const deleting = ref(false);

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('Token found:', !!token);

        if (!token) {
          throw new Error('No authentication token found');
        }

        console.log('Fetching walkthroughs data...');
        const response = await axios.get('https://api.kewaar.com/api/admin/walkthroughs1', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        console.log('API Response:', response.data);

        if (response.data && response.data.status === 'success' && response.data.data) {
          landings.value = response.data.data.landings || [];
          landingItems.value = response.data.data.landingItems || [];
        } else {
          throw new Error('Invalid data format received from API');
        }

        console.log('Processed landings:', landings.value);
        console.log('Processed landing items:', landingItems.value);
      } catch (err) {
        console.error('Detailed error:', err.response || err);
        error.value = err.response?.data?.message || err.message || 'Failed to fetch data';
      } finally {
        loading.value = false;
      }
    };

    const getLandingTitle = (landingId) => {
      const landing = landings.value.find(l => l.id === landingId);
      return landing ? landing.title : 'Unknown Landing';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString();
    };

    const openEditModal = (item) => {
      editingItem.value = { ...item };
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      editingItem.value = null;
    };

    const openCreateModal = () => {
      // Set default landing_id if available
      if (landings.value.length > 0) {
        newItem.value.landing_id = landings.value[0].id;
      }
      showCreateModal.value = true;
    };

    const closeCreateModal = () => {
      showCreateModal.value = false;
      // Reset form
      newItem.value = {
        landing_id: landings.value.length > 0 ? landings.value[0].id : 1,
        icon: '',
        text: '',
        text_ar: ''
      };
    };

    const confirmDelete = (item) => {
      itemToDelete.value = item;
      showDeleteModal.value = true;
    };

    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      itemToDelete.value = null;
    };

    const handleSubmit = async () => {
      if (!editingItem.value) return;

      saving.value = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/landing-items/${editingItem.value.id}`,
          {
            landing_id: editingItem.value.landing_id,
            icon: editingItem.value.icon,
            text: editingItem.value.text,
            text_ar: editingItem.value.text_ar
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.status === 'success') {
          // Update the item in the local array
          const index = landingItems.value.findIndex(item => item.id === editingItem.value.id);
          if (index !== -1) {
            landingItems.value[index] = { ...editingItem.value };
          }
          closeEditModal();
        } else {
          throw new Error('Failed to update landing item');
        }
      } catch (err) {
        console.error('Update error:', err);
        error.value = err.response?.data?.message || err.message || 'Failed to update landing item';
      } finally {
        saving.value = false;
      }
    };

    const handleCreateSubmit = async () => {
      creating.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          'https://api.kewaar.com/api/admin/landing-items',
          {
            landing_id: newItem.value.landing_id,
            icon: newItem.value.icon,
            text: newItem.value.text,
            text_ar: newItem.value.text_ar
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.status === 'success') {
          // Add the new item to the array with the returned data
          if (response.data.data) {
            landingItems.value.push(response.data.data);
          }
          closeCreateModal();
        } else {
          throw new Error('Failed to create landing item');
        }
      } catch (err) {
        console.error('Create error:', err);
        error.value = err.response?.data?.message || err.message || 'Failed to create landing item';
      } finally {
        creating.value = false;
      }
    };
    
    const handleDelete = async () => {
      if (!itemToDelete.value) return;
      
      deleting.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `https://api.kewaar.com/api/admin/landing-items/${itemToDelete.value.id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.status === 'success') {
          // Remove the item from the local array
          landingItems.value = landingItems.value.filter(item => item.id !== itemToDelete.value.id);
          closeDeleteModal();
        } else {
          throw new Error('Failed to delete landing item');
        }
      } catch (err) {
        console.error('Delete error:', err);
        error.value = err.response?.data?.message || err.message || 'Failed to delete landing item';
      } finally {
        deleting.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      landingItems,
      landings,
      loading,
      error,
      formatDate,
      getLandingTitle,
      showEditModal,
      showCreateModal,
      showDeleteModal,
      editingItem,
      newItem,
      itemToDelete,
      saving,
      creating,
      deleting,
      openEditModal,
      closeEditModal,
      openCreateModal,
      closeCreateModal,
      confirmDelete,
      closeDeleteModal,
      handleSubmit,
      handleCreateSubmit,
      handleDelete,
      toolbarOptions
    };
  }
};
</script>

<style scoped>
.layout-container {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  margin-left: 280px; /* Match sidebar width */
  padding: 2rem;
  background-color: #f8fafc;
}

.landing-items-container {
  max-width: 1200px;
  margin: 0 auto;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0;
}

.create-button {
  background: #3B82F6;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-button:hover {
  background: #2563EB;
}

.create-button .icon {
  font-size: 1.25rem;
  line-height: 1;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.landing-items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.landing-items-table th,
.landing-items-table td {
  padding: 1rem;
  text-align: left;
}

.landing-items-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #1a1f2e;
}

.landing-items-table tr:hover {
  background: #f8fafc;
}

.actions-column {
  display: flex;
  gap: 0.5rem;
}

.edit-button,
.delete-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.edit-button {
  color: #3B82F6;
}

.delete-button {
  color: #EF4444;
}

.edit-button:hover {
  background: rgba(59, 130, 246, 0.1);
}

.delete-button:hover {
  background: rgba(239, 68, 68, 0.1);
}

.edit-button svg,
.delete-button svg {
  width: 18px;
  height: 18px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.delete-modal {
  max-width: 500px;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1a1f2e;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
}

.modal-body {
  padding: 1.5rem;
}

.delete-warning {
  font-size: 1.1rem;
  font-weight: 500;
  color: #1a1f2e;
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.delete-details {
  color: #64748b;
  margin-top: 0;
  margin-bottom: 1.5rem;
}

.item-preview {
  background: #f8fafc;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.preview-row {
  margin-bottom: 0.5rem;
}

.preview-row:last-child {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #1a1f2e;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.help-text {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: #64748b;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-button,
.save-button,
.delete-confirm-button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-button {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.save-button {
  background: #3B82F6;
  border: none;
  color: white;
}

.delete-confirm-button {
  background: #EF4444;
  border: none;
  color: white;
}

.cancel-button:hover {
  background: #e2e8f0;
}

.save-button:hover {
  background: #2563EB;
}

.delete-confirm-button:hover {
  background: #DC2626;
}

.save-button:disabled,
.delete-confirm-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.no-data {
  text-align: center;
  color: #64748b;
  padding: 2rem;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}

.error {
  text-align: center;
  padding: 2rem;
  color: #ef4444;
  background: #fee2e2;
  border-radius: 8px;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 1rem;
  }

  .landing-items-table {
    font-size: 0.8rem;
  }

  .landing-items-table th,
  .landing-items-table td {
    padding: 0.75rem;
  }

  .modal-content {
    width: 95%;
  }
  
  .header-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .create-button {
    width: 100%;
    justify-content: center;
  }
}
</style> 